"use client"
import { useMemo } from "react"
import { Box, useTheme } from "@mui/material"

const ImageFanDisplay = ({
  images = [],
  position = "left", // 'left', 'right', or 'both'
  maxWidth = "250px",
  offsetX = "0",
  zIndex = 0,
  rotationRange = 40, // Maximum rotation in degrees
  scaleConfig = { normal: 1, small: 0.6 }, // Scale factors
}) => {
  const theme = useTheme()

  // Function to determine if an image is a config image
  const isConfigImage = (path) => path.includes("/config/")

  // Create shuffled and split image arrays for left and right sides
  const { leftImages, rightImages } = useMemo(() => {
    if (images.length === 0) return { leftImages: [], rightImages: [] }

    // Shuffle the array
    const shuffled = [...images].sort(() => Math.random() - 0.5)

    // If position is 'both', split the images between left and right
    if (position === "both") {
      const midpoint = Math.ceil(shuffled.length / 2)
      return {
        leftImages: shuffled.slice(0, midpoint),
        rightImages: shuffled.slice(midpoint),
      }
    }

    // If position is 'left' or 'right', use all images for that side
    return {
      leftImages: position === "left" ? shuffled : [],
      rightImages: position === "right" ? shuffled : [],
    }
  }, [images, position])

  // Render the fan display
  return (
    <>
      {/* Left side images */}
      {(position === "left" || position === "both") && (
        <Box
          sx={{
            position: "absolute",
            left: offsetX,
            top: 0,
            bottom: 0,
            width: maxWidth,
            zIndex: zIndex,
            pointerEvents: "none",
          }}
        >
          {leftImages.map((img, index) => {
            const isConfig = isConfigImage(img)
            const rotation = -10 + ((index * 5) % rotationRange) - rotationRange / 2 // Vary rotation
            const scale = isConfig ? scaleConfig.small : scaleConfig.normal // Make config images smaller

            return (
              <Box
                key={`left-${img}-${index}`}
                component="img"
                src={img}
                alt={`Bot screenshot ${index}`}
                sx={{
                  position: "absolute",
                  width: isConfig ? "70%" : "90%",
                  maxWidth: "200px",
                  height: "auto",
                  left: `${(index * 10) % 30}%`,
                  top: `${((index * 15) % 70) + 10}%`,
                  transform: `rotate(${rotation}deg) scale(${scale})`,
                  transformOrigin: "center center",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
                  border: "1px solid rgba(0, 229, 255, 0.3)",
                  borderRadius: "4px",
                  opacity: 0.8,
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  pointerEvents: "auto",
                  "&:hover": {
                    opacity: 1,
                    transform: `rotate(${rotation}deg) scale(${scale * 1.3})`,
                    zIndex: 100,
                    boxShadow: "0 0 15px 5px rgba(0, 229, 255, 0.7)",
                    border: "1px solid rgba(0, 229, 255, 0.8)",
                  },
                }}
              />
            )
          })}
        </Box>
      )}

      {/* Right side images */}
      {(position === "right" || position === "both") && (
        <Box
          sx={{
            position: "absolute",
            right: offsetX,
            top: 0,
            bottom: 0,
            width: maxWidth,
            zIndex: zIndex,
            pointerEvents: "none",
          }}
        >
          {rightImages.map((img, index) => {
            const isConfig = isConfigImage(img)
            const rotation = -10 + ((index * 5) % rotationRange) - rotationRange / 2 // Vary rotation
            const scale = isConfig ? scaleConfig.small : scaleConfig.normal // Make config images smaller

            return (
              <Box
                key={`right-${img}-${index}`}
                component="img"
                src={img}
                alt={`Bot screenshot ${index}`}
                sx={{
                  position: "absolute",
                  width: isConfig ? "70%" : "90%",
                  maxWidth: "200px",
                  height: "auto",
                  right: `${(index * 10) % 30}%`,
                  top: `${((index * 15) % 70) + 10}%`,
                  transform: `rotate(${rotation}deg) scale(${scale})`,
                  transformOrigin: "center center",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
                  border: "1px solid rgba(0, 229, 255, 0.3)",
                  borderRadius: "4px",
                  opacity: 0.8,
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  pointerEvents: "auto",
                  "&:hover": {
                    opacity: 1,
                    transform: `rotate(${rotation}deg) scale(${scale * 1.3})`,
                    zIndex: 100,
                    boxShadow: "0 0 15px 5px rgba(0, 229, 255, 0.7)",
                    border: "1px solid rgba(0, 229, 255, 0.8)",
                  },
                }}
              />
            )
          })}
        </Box>
      )}
    </>
  )
}

export default ImageFanDisplay
